/**

QtySelector class provides functionality to select a quantity by adding or subtracting from an input field.

@class
@param {HTMLFormElement} form - The form element containing the quantity selector inputs.
@property {HTMLFormElement} form - The form element containing the quantity selector inputs.
@property {number} total - The current quantity selected.
@property {HTMLInputElement} value_display - The input field displaying the selected quantity.
@property {Object} controls - The controls for adding and subtracting from the selected quantity.
@property {HTMLButtonElement} controls.add - The button element for adding to the selected quantity.
@property {HTMLButtonElement} controls.subtract - The button element for subtracting from the selected quantity.

*/

export class QtySelector {
  /**
   * Creates a new `QtySelector` instance.
   *
   * @param {HTMLElement} form - The HTML form element that contains the quantity selector.
   */
  constructor(form) {
    (this.form = form),
      (this.total = 1),
      (this.value_display = form.querySelector(".quantity__input")),
      (this.controls = {
        add: form.querySelector("qty-add"),
        subtract: form.querySelector("qty-subtract"),
      });

    if (this.value_display && this.controls.add && this.controls.subtract) {
      this._init();
    } else {
      throw new Error("Quantity Selector Elements Missing");
    }
  }

  /**
   * Increases the current quantity value by 1, up to a maximum of 99.
   *
   */
  _increase = () => {
    if (this.total < 99) {
      this.total += 1;
      this.value_display.value = this.total;
      this.value_display.setAttribute("aria-valuenow", this.total);
    }
  };

  /**
   * Decreases the current quantity value by 1, down to a minimum of 1.
   *
   */
  _decrease = () => {
    if (this.total > 1) {
      this.total -= 1;
      this.value_display.value = this.total;
      this.value_display.setAttribute("aria-valuenow", this.total);
    }
  };

  /**
   * Returns the current quantity value.
   *
   * @returns {number} - The current quantity value.
   */
  _getTotal = () => {
    return this.total;
  };

  /**
   * Refreshes the `QtySelector` instance with a new HTML form element that contains the quantity selector.
   *
   * @param {HTMLElement} form - The new HTML form element that contains the quantity selector.
   */
  _refresh = (form) => {
    this.form = form;
    (this.value_display = form.querySelector(".quantity__input")),
      (this.controls = {
        add: form.querySelector("qty-add"),
        subtract: form.querySelector("qty-subtract"),
      });

    if (this.value_display && this.controls.add && this.controls.subtract) {
      this.value_display.value = this.total;
      this._addControls();
    } else {
      throw new Error("Quantity Selector Elements Missing");
    }
  };

  /**
   * Adds event listeners to the quantity selector controls.
   */
  _addControls = () => {
    this.controls.add.addEventListener("click", this._increase);
    this.controls.add.addEventListener("keydown", (e) => {
      if (e.key == "Enter") {
        this._increase();
      }
    });
    this.controls.subtract.addEventListener("click", this._decrease);
    this.controls.subtract.addEventListener("keydown", (e) => {
      if (e.key == "Enter") {
        this._decrease();
      }
    });
  };

  /**
   * Initializes the `QtySelector` instance by adding event listeners to the quantity selector controls.
   */
  _init = () => {
    this._addControls();
  };
}
